import { Button, ButtonGroup, Grid } from '@design-systems/canvas-ui'
import { useEffect, useState } from 'react'
import { Table } from 'components/table-compat/table-compat'
import { TableData, buildDataTableObject } from 'utils/data'
import { useSwipeToIndex } from './useSwipeToIndex'
import './generalized-component.scss'
import { ErrorAlert } from 'components/error-fallback'
import { useLogClick } from 'utils/analytics'
import { TimePeriods } from 'components/timeperiods/timeperiods'
import { formatNegative } from 'utils/number-format'

// Drill breadcrumbs and drillable rows always live in the first column. Matching
// on the index keeps drill handling independent of the translated heading text.
const DRILL_COLUMN_INDEX = 0

// Utility type to require `error` prop when `isError` is true
type RequireErrorWhenIsError<T extends { isError?: boolean; error?: Error }> =
    T['isError'] extends true ? T & { error: Error } : T

export type GeneralizedComponentViewByOption =
    | 'view in $'
    | 'view in %'
    | 'comp %'
    | 'show FDC'
    | 'show RDC'
    | 'show stores'
    | 'show districts'
    | ''

export type GeneralizedViewOption = {
    title: string
    subtitle: string
    data: {
        [key: string]: TableData
    }
}
export type GeneralizedComponentPayloadType = Partial<{
    [key in GeneralizedComponentViewByOption]: Array<GeneralizedViewOption>
}>

interface IGeneralizedComponentProps {
    payload: GeneralizedComponentPayloadType
    title?: string
    titleClassName?: string
    subTitle?: string
    scrollableWidth?: boolean
    sortable?: boolean
    widths?: number[]
    pinFirstColumn?: boolean
    parentHandleClick?: (timePeriodIndex: number, tabsIndex: number) => void
    selectedTimeIndex?: number
    selectedTabIndex?: number
    onDrillUpdate?: (data: string[]) => void
    onIndexUpdate?: (index: number) => void
    isLoading?: boolean
    loadingComponent?: React.ReactNode
    expandedWidth?: boolean
    isError?: boolean
    isNoData?: boolean
    error?: Error
    children?: React.ReactNode
    alternateRowColor?: boolean
    onSort?: (sortDirection: 'asc' | 'desc', field: string) => void
    columnSize?: number
    hideTable?: boolean
    kpiName?: string
    columnSortSettingsByIndex?: {
        [columnIndex: number]: {
            sortable?: boolean
            sortType?: 'character' | 'numeric' | 'date' | 'boolean'
            defaultSort?: 'dsc' | 'asc'
            onSort?: (sortDirection: 'asc' | 'dsc', field: string) => void
        }
    }
    isTimePeriodSticky?: boolean
    isTimePeriodOutsideHeader?: boolean
}

export type GeneralizedComponentProps =
    RequireErrorWhenIsError<IGeneralizedComponentProps>

export const GeneralizedComponent = ({
    payload,
    title,
    titleClassName = '',
    subTitle,
    scrollableWidth = true,
    sortable = false,
    widths = [100, 100, 100, 100, 100, 100, 100, 100, 100],
    pinFirstColumn = true,
    parentHandleClick,
    selectedTimeIndex = 0,
    selectedTabIndex = 0,
    onDrillUpdate,
    onIndexUpdate,
    isLoading,
    loadingComponent,
    expandedWidth = true,
    isError = false,
    isNoData = false,
    error,
    children = null,
    alternateRowColor = true,
    onSort,
    columnSize,
    hideTable,
    kpiName = 'generalized component',
    columnSortSettingsByIndex,
    isTimePeriodSticky = true,
    isTimePeriodOutsideHeader = false,
}: GeneralizedComponentProps) => {
    const views = Object.keys(payload ?? {})
    const [viewIndex, setViewIndex] = useState(0)
    const currentView = views[viewIndex] as GeneralizedComponentViewByOption
    const currentViewData = payload?.[currentView]
    const [timePeriodIndex, setTimePeriodIndex] =
        useState<number>(selectedTimeIndex)
    const [tabsIndex, setTabsIndex] = useState<number>(selectedTabIndex)
    const [selectedCell, setSelectedCell] = useState<string>('')
    const [drills, setDrills] = useState<string[]>([])
    const { logClick } = useLogClick(kpiName)

    const timePeriods = Array.isArray(currentViewData)
        ? currentViewData.map((tp) => tp?.title ?? '')
        : []

    const currentViewDataForTimePeriod = currentViewData?.[timePeriodIndex]
    const { data = {} } = currentViewDataForTimePeriod ?? {}

    const tabs = Object.keys(data ?? {})
    const currentTab = tabs[tabsIndex]
    const currentViewDataForTimePeriodAndTab: TableData = data?.[
        currentTab
    ] ?? { rows: [] }

    const { handleTouchStart, handleTouchMove, handleTouchEnd } =
        useSwipeToIndex({
            timePeriods,
            setTimePeriodIndex,
        })

    useEffect(() => {
        if (viewIndex >= views.length) {
            setViewIndex(0)
        }
    }, [payload, viewIndex, views.length])

    useEffect(() => {
        onIndexUpdate?.(timePeriodIndex)
    }, [timePeriodIndex, onIndexUpdate])

    useEffect(() => {
        if (parentHandleClick) {
            parentHandleClick(timePeriodIndex, tabsIndex)
        }
    }, [timePeriodIndex, tabsIndex, parentHandleClick])

    useEffect(() => {
        const tabsIndexWithDefaultFallback =
            tabs.length <= tabsIndex ? 0 : tabsIndex
        setTabsIndex(tabsIndexWithDefaultFallback)
    }, [timePeriodIndex, tabsIndex, tabs.length])

    const handleClick = (selected: string) => {
        setSelectedCell(selected)

        const rows = currentViewDataForTimePeriodAndTab?.rows ?? []
        const selectedIndex = rows.findIndex(
            (row) =>
                formatNegative(row?.[DRILL_COLUMN_INDEX]?.value ?? '') ===
                selected,
        )

        if (selectedIndex === -1) return

        // Rows 0..drills.length-1 are the ancestor breadcrumbs returned above the
        // current level, so clicking one collapses the trail back to that depth.
        const newDrills =
            selectedIndex < drills.length
                ? drills.slice(0, selectedIndex)
                : [...drills, selected]

        setDrills(newDrills)
        onDrillUpdate?.(newDrills)
    }

    const dataObject = buildDataTableObject({
        currentData: currentViewDataForTimePeriodAndTab,
        widths,
        pinFirstColumn,
        sortable,
        handleClick,
        selectedCell,
        isLoading,
        onSort,
        columnSortSettingsByIndex,
    })

    const renderTimePeriods = timePeriods.length > 1
    const renderDataTabs = tabs && tabs.length > 1

    const timePeriodControls = renderTimePeriods ? (
        <TimePeriods
            timePeriods={timePeriods}
            activeTimePeriod={timePeriods[timePeriodIndex]}
            onTabSelect={(timePeriod, index) => {
                setTimePeriodIndex(index)
            }}
            className="time-periods kpi hc8-pb-1x"
            isTimePeriodSticky={isTimePeriodSticky}
            page={kpiName}
        />
    ) : null

    const nextIndex = (viewIndex + 1) % views.length

    // This row holds the title, the view-by buttons and the time period bar.
    // When the first two are absent the padding is pure dead space above the
    // bar, which leaves it floating below the page tabs instead of sitting
    // flush against them.
    const headerRowClassName =
        title || views.length > 1 ? 'hc8-pt-4x' : undefined

    return (
        <div
            className="generalized-component"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {isTimePeriodOutsideHeader && timePeriodControls}
            <Grid.Container
                align="center"
                justify="space-between"
                className={headerRowClassName}
            >
                {title && (
                    <GeneralizedComponent.Title
                        title={title}
                        titleClassName={titleClassName}
                        subTitle={subTitle}
                        columnSize={columnSize}
                    />
                )}

                {/* Spacer to push TimePeriodButtons to the right when no title is present */}
                {!title && views.length > 1 && (
                    <Grid.Item
                        size={{
                            xs: 8,
                        }}
                    />
                )}

                {views.length > 1 && (
                    <GeneralizedComponent.TimePeriodButtons
                        views={views}
                        nextIndex={nextIndex}
                        setViewIndex={setViewIndex}
                        logClick={logClick}
                        kpiName={kpiName}
                    />
                )}

                {!isTimePeriodOutsideHeader && timePeriodControls}
            </Grid.Container>
            {renderDataTabs && (
                <GeneralizedComponent.DataTabs
                    tabs={tabs}
                    tabsIndex={tabsIndex}
                    setTabsIndex={setTabsIndex}
                    kpiName={kpiName}
                    logClick={logClick}
                />
            )}
            {!isError && !isNoData && children && <>{children}</>}
            {isError ? (
                <ErrorAlert error={error as Error} className="hc8-mv-6x" />
            ) : isNoData ? (
                <ErrorAlert variant="no-data" className="hc8-mv-6x" />
            ) : (
                <Grid.Container justify="center" className="hc8-pv-3x">
                    <Grid.Item
                        className={expandedWidth ? 'hc8-ph-none' : ''}
                        size={{
                            xs: 12,
                        }}
                    >
                        {isLoading && loadingComponent}
                        {!isLoading && dataObject?.rowData && !hideTable && (
                            <Table
                                data={dataObject}
                                name={title ?? 'Generalized Component'}
                                scrollableWidth={scrollableWidth}
                                alternateRowColor={alternateRowColor}
                            />
                        )}
                    </Grid.Item>
                </Grid.Container>
            )}
        </div>
    )
}

GeneralizedComponent.TimePeriodButtons = ({
    views,
    nextIndex,
    setViewIndex,
    logClick,
    kpiName = 'generalized component',
}: {
    views: string[]
    nextIndex: number
    setViewIndex: (index: number) => void
    logClick: (message: string) => void
    kpiName?: string
}) => {
    return (
        <Grid.Item
            className="hc8-pr-none hc8-ta-right"
            size={{
                xs: 4,
            }}
        >
            <Button
                onClick={() => {
                    setViewIndex(nextIndex)
                    logClick(`${kpiName} ${views[nextIndex]}`)
                }}
                className="hc8-fs-050 hc8-ph-none hc8-pr-x"
                variant="ghost"
            >
                {views[nextIndex]}
            </Button>
        </Grid.Item>
    )
}

type SharedProps = {
    items: string[]
    selected: number
    setSelected: (value: number) => void
    className?: string
    testId?: string
    kpiName?: string
    logClick: (message: string) => void
}

GeneralizedComponent.Buttons = ({
    items,
    selected,
    setSelected,
    className,
    testId,
    kpiName = 'generalized component',
    logClick,
}: SharedProps) => {
    return (
        <ButtonGroup
            className={className ? className : ''}
            data-testid={testId}
        >
            {items.map((item: string, index: number) => {
                return (
                    <Button
                        variant={index === selected ? 'primary' : 'secondary'}
                        onClick={() => {
                            setSelected(index)
                            logClick(`${kpiName} ${item}`)
                        }}
                        key={items[index]}
                    >
                        {item}
                    </Button>
                )
            })}
        </ButtonGroup>
    )
}

type DataTabsProps = {
    tabs: string[]
    tabsIndex: number
    setTabsIndex: (index: number) => void
    kpiName: string
    logClick: (message: string) => void
}

type TitleProps = {
    title: string
    titleClassName?: string
    subTitle?: string
    columnSize?: number
}

GeneralizedComponent.Title = ({
    title,
    titleClassName = '',
    subTitle,
    columnSize,
}: TitleProps) => (
    <Grid.Item
        className={`${titleClassName} title-container hc8-pl-none`}
        data-testid={title}
        size={{
            xs: columnSize !== undefined ? columnSize : 8,
        }}
    >
        <span>{title}</span>
        <small className="hc8-fs-050">{subTitle}</small>
    </Grid.Item>
)

GeneralizedComponent.DataTabs = ({
    tabs,
    tabsIndex,
    setTabsIndex,
    kpiName,
    logClick,
}: DataTabsProps) => {
    return (
        <Grid.Container
            align="center"
            justify="center"
            className="hc8-mb-3x hc8-mt-2x"
        >
            <Grid.Item>
                <GeneralizedComponent.Buttons
                    items={tabs}
                    selected={tabsIndex}
                    setSelected={setTabsIndex}
                    key="data-tabs"
                    className="data-tabs"
                    kpiName={kpiName}
                    logClick={logClick}
                />
            </Grid.Item>
        </Grid.Container>
    )
}
